import { CACHE_EXPIRATION_TIME } from "@/data_source/constants/env";

import { type RequestResponse } from ".";

class CacheEntry<T> {
  private readonly _value: T;
  get value() {
    return this._value;
  }
  private readonly timestamp: number;
  constructor(value: T) {
    this._value = value;
    this.timestamp = Date.now();
  }

  isExpired() {
    return CACHE_EXPIRATION_TIME <= 0
      ? true
      : Date.now() - this.timestamp > CACHE_EXPIRATION_TIME;
  }
}

class RequestCacheRegistry {
  private cache: Map<string, CacheEntry<RequestResponse<any>>>;

  constructor() {
    this.cache = new Map();
  }

  set<T>(key: string, value: RequestResponse<T>) {
    this.cache.set(key, new CacheEntry(value));
  }

  get<T>(key: string): CacheEntry<RequestResponse<T>> | undefined {
    return this.cache.get(key);
  }

  has(key: string) {
    return !!(
      this.cache.has(key) &&
      !this.cache.get(key)?.isExpired() &&
      this.cache.get(key)?.value
    );
  }

  delete(key: string) {
    this.cache.delete(key);
  }

  deleteMatching(predicate: (key: string) => boolean) {
    this.cache.forEach((_, key) => {
      if (predicate(key)) this.cache.delete(key);
    });
  }

  clear() {
    this.cache.clear();
  }
}

class RequestCacheProxy {
  private registry: RequestCacheRegistry;
  constructor() {
    this.registry = new RequestCacheRegistry();
  }

  set<T>(key: string, value: RequestResponse<T>) {
    this.registry.set(key, value);
  }

  get<T>(key: string): RequestResponse<T> | undefined {
    const entry = this.registry.get<T>(key);
    if (entry && entry.isExpired()) {
      const value = entry.value;
      this.registry.delete(key);
      return value;
    }
    return entry?.value;
  }

  has(key: string) {
    return this.registry.has(key);
  }

  delete(key: string) {
    this.registry.delete(key);
  }

  deleteMatching(predicate: (key: string) => boolean) {
    this.registry.deleteMatching(predicate);
  }

  clear() {
    this.registry.clear();
  }
}
export class RequestCacheBuilder {
  private static _instance: RequestCacheBuilder;
  private cache: RequestCacheProxy;
  private constructor() {
    this.cache = new RequestCacheProxy();
  }
  static getInstance(): RequestCacheProxy {
    if (!RequestCacheBuilder._instance) {
      RequestCacheBuilder._instance = new RequestCacheBuilder();
    }
    return RequestCacheBuilder._instance.cache;
  }
}
